import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, Mail, KeyRound, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELDS_LAYOUT = [
  {
    section: 'Datos Generales',
    fields: [
      { name: 'nombre_razon_social', label: 'Nombre / Razón Social', type: 'text', required: true },
      { name: 'cuit_cuil', label: 'CUIT / CUIL', type: 'text' },
    ],
  },
  {
    section: 'Contacto',
    fields: [
      { name: 'telefono', label: 'Teléfono', type: 'text' },
      { name: 'whatsapp', label: 'WhatsApp', type: 'text' },
      { name: 'direccion', label: 'Dirección', type: 'text' },
      { name: 'localidad', label: 'Localidad', type: 'text' },
      { name: 'provincia', label: 'Provincia', type: 'text' },
      { name: 'codigo_postal', label: 'Código Postal', type: 'text' },
      {
        name: 'preferencia_contacto',
        label: 'Preferencia de Contacto',
        type: 'select',
        options: [
          { value: 'email', label: 'Email' },
          { value: 'telefono', label: 'Teléfono' },
          { value: 'whatsapp', label: 'WhatsApp' },
        ],
      },
    ],
  },
  {
    section: 'Datos Fiscales',
    fields: [
      {
        name: 'condicion_iva',
        label: 'Condición IVA',
        type: 'select',
        options: [
          { value: '', label: '— Seleccionar —' },
          { value: 'responsable_inscripto', label: 'Responsable Inscripto' },
          { value: 'monotributista', label: 'Monotributista' },
          { value: 'exento', label: 'Exento' },
          { value: 'consumidor_final', label: 'Consumidor Final' },
        ],
      },
      { name: 'domicilio_fiscal', label: 'Domicilio Fiscal', type: 'text' },
      {
        name: 'tipo_factura',
        label: 'Tipo de Factura',
        type: 'select',
        options: [
          { value: '', label: '— Seleccionar —' },
          { value: 'A', label: 'A' },
          { value: 'B', label: 'B' },
          { value: 'C', label: 'C' },
        ],
      },
    ],
  },
  {
    section: 'Datos Bancarios (opcional)',
    fields: [
      { name: 'cbu', label: 'CBU', type: 'text' },
      { name: 'alias_cbu', label: 'Alias CBU', type: 'text' },
      { name: 'banco', label: 'Banco', type: 'text' },
      { name: 'titular_cuenta', label: 'Titular de Cuenta', type: 'text' },
    ],
  },
  {
    section: 'Notas',
    fields: [
      { name: 'notas', label: 'Notas / Observaciones', type: 'textarea' },
    ],
  },
];

export default function ActivarPropietarioPage() {
  const navigate = useNavigate();
  // 'email' | 'form' | 'reset' | 'already_active' | 'done'
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [propietarioId, setPropietarioId] = useState(null);
  const [formData, setFormData] = useState({});
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Ingresá tu email');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/propietarios/activacion/check-email`, {
        email: email.trim().toLowerCase(),
      });
      const estado = res.data.estado;
      // Caso C: ya activada -> mostrar pantalla "ir a login"
      if (estado === 'activo') {
        setStep('already_active');
        return;
      }
      // Caso D: requiere cambio de password -> ir directo al paso de reset
      if (res.data.ok && estado === 'requiere_cambio_password') {
        setPropietarioId(res.data.propietario_id);
        setFormData(res.data.datos || {});
        setStep('reset');
        return;
      }
      // Caso B: pendiente -> formulario completo
      if (res.data.ok && estado === 'pendiente_activacion') {
        setPropietarioId(res.data.propietario_id);
        setFormData(res.data.datos || {});
        setStep('form');
        return;
      }
      // Caso A (no_existe / conflicto / fallback): mensaje neutro
      setError(res.data.message || 'No pudimos validar el acceso. Revisá que estés usando el mismo email registrado.');
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || 'Error de conexión.';
      setError(typeof msg === 'string' ? msg : 'Error al validar el email.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.nombre_razon_social?.trim()) {
      setError('El nombre/razón social es obligatorio.');
      return;
    }
    if (!password || password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/propietarios/activacion/completar`, {
        propietario_id: propietarioId,
        email: email.trim().toLowerCase(),
        datos: formData,
        password,
      });
      toast.success('¡Tu cuenta fue activada!');
      setStep('done');
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || 'Error al activar la cuenta.';
      setError(typeof msg === 'string' ? msg : 'Error al activar la cuenta.');
    } finally {
      setLoading(false);
    }
  };

  // Solo cambia password (caso D: reset solicitado por admin).
  const handleSubmitReset = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/propietarios/activacion/completar`, {
        propietario_id: propietarioId,
        email: email.trim().toLowerCase(),
        datos: {},
        password,
      });
      toast.success('Contraseña actualizada');
      setStep('done');
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || 'Error al actualizar la contraseña.';
      setError(typeof msg === 'string' ? msg : 'Error al actualizar la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="flex flex-col items-center mb-8" data-testid="activar-header">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Activar mi cuenta
          </h1>
          <p className="text-muted-foreground mt-2 text-center">
            Completá tus datos y creá tu contraseña para acceder a la app.
          </p>
        </div>

        <div className="bg-white border border-border rounded-2xl shadow-xl p-8">
          {/* STEP 1: Email */}
          {step === 'email' && (
            <form onSubmit={handleCheckEmail} className="space-y-5" data-testid="activar-form-email">
              <div>
                <Label htmlFor="email" className="text-sm font-medium">
                  Ingresá tu email
                </Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tuemail@dominio.com"
                    className="pl-9"
                    autoFocus
                    data-testid="activar-input-email"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Usá el email con el que figurás registrado como propietario.
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="activar-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-full" data-testid="activar-btn-validar">
                {loading ? 'Validando...' : 'Continuar'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>
          )}

          {/* STEP 2: Formulario completo */}
          {step === 'form' && (
            <form onSubmit={handleSubmitForm} className="space-y-6" data-testid="activar-form-datos">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-800 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Tu email <strong>{email}</strong> está autorizado. Completá tu ficha y creá tu contraseña.
                </span>
              </div>

              {FIELDS_LAYOUT.map((section) => (
                <div key={section.section}>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                    {section.section}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields.map((f) => (
                      <div key={f.name} className={f.type === 'textarea' ? 'md:col-span-2' : ''}>
                        <Label htmlFor={f.name} className="text-sm">
                          {f.label} {f.required && <span className="text-red-500">*</span>}
                        </Label>
                        {f.type === 'select' ? (
                          <Select
                            value={formData[f.name] || ''}
                            onValueChange={(v) => setFormData({ ...formData, [f.name]: v })}
                          >
                            <SelectTrigger className="mt-1" data-testid={`activar-${f.name}`}>
                              <SelectValue placeholder="— Seleccionar —" />
                            </SelectTrigger>
                            <SelectContent>
                              {f.options.map((o) => (
                                <SelectItem key={o.value || 'empty'} value={o.value || '__empty__'}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : f.type === 'textarea' ? (
                          <Textarea
                            id={f.name}
                            value={formData[f.name] || ''}
                            onChange={(e) => setFormData({ ...formData, [f.name]: e.target.value })}
                            className="mt-1"
                            rows={3}
                            data-testid={`activar-${f.name}`}
                          />
                        ) : (
                          <Input
                            id={f.name}
                            type={f.type}
                            value={formData[f.name] || ''}
                            onChange={(e) => setFormData({ ...formData, [f.name]: e.target.value })}
                            className="mt-1"
                            data-testid={`activar-${f.name}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Contraseña */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                  Crear Contraseña
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pwd" className="text-sm">
                      Contraseña <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative mt-1">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="pwd"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-9"
                        placeholder="Mínimo 8 caracteres"
                        data-testid="activar-input-password"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="pwd2" className="text-sm">
                      Repetir Contraseña <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="pwd2"
                      type="password"
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      className="mt-1"
                      data-testid="activar-input-password-confirm"
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="activar-error-form">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setStep('email');
                    setError('');
                  }}
                  data-testid="activar-btn-volver"
                >
                  Volver
                </Button>
                <Button type="submit" disabled={loading} className="flex-1" data-testid="activar-btn-completar">
                  {loading ? 'Activando...' : 'Activar mi cuenta'}
                </Button>
              </div>
            </form>
          )}

          {/* STEP: Ya activada (caso C) */}
          {step === 'already_active' && (
            <div className="text-center py-8" data-testid="activar-already-active">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
                Esta cuenta ya fue activada
              </h2>
              <p className="text-muted-foreground mb-6">
                Ingresá desde la pantalla de login.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full max-w-xs"
                data-testid="activar-btn-ir-login"
              >
                Ir a iniciar sesión
              </Button>
            </div>
          )}

          {/* STEP: Reset (caso D) - solo nueva contraseña */}
          {step === 'reset' && (
            <form onSubmit={handleSubmitReset} className="space-y-5" data-testid="activar-form-reset">
              <div className="bg-orange-50 border border-orange-200 rounded-md p-3 text-sm text-orange-800 flex items-start gap-2">
                <KeyRound className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Tu cuenta requiere una nueva contraseña. Creala abajo para volver a ingresar.
                </span>
              </div>
              <div>
                <Label htmlFor="pwd-reset" className="text-sm">
                  Nueva contraseña <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="pwd-reset"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9"
                    placeholder="Mínimo 8 caracteres"
                    data-testid="reset-input-password"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="pwd-reset2" className="text-sm">
                  Repetir contraseña <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="pwd-reset2"
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  className="mt-1"
                  data-testid="reset-input-password-confirm"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="reset-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setStep('email'); setError(''); setPassword(''); setPasswordConfirm(''); }}
                  data-testid="reset-btn-volver"
                >
                  Volver
                </Button>
                <Button type="submit" disabled={loading} className="flex-1" data-testid="reset-btn-guardar">
                  {loading ? 'Guardando...' : 'Crear nueva contraseña'}
                </Button>
              </div>
            </form>
          )}

          {/* STEP 3: Listo */}
          {step === 'done' && (
            <div className="text-center py-8" data-testid="activar-done">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
                ¡Listo!
              </h2>
              <p className="text-muted-foreground mb-6">
                Tu cuenta fue activada. Ya podés iniciar sesión.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full max-w-xs" data-testid="activar-btn-login">
                Ir a Iniciar Sesión
              </Button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          ¿Necesitás ayuda? Contactá a la administración.
        </p>
      </div>
    </div>
  );
}
